/**
 * Point Draw next-leg route solver (pure).
 *
 * Evaluates routing patterns from the current head + travel direction toward a
 * cursor, and picks the simplest legal pattern.
 *
 * Patterns (simplest → more complex):
 *   A  straight
 *   B  turn90          (Curve90 + Straight) — ordinary corner
 *   C  straight_turn90 (Straight + Curve90 + Straight) — corner where the cursor turned
 *   D  curve180        (single Curve180 + Straight) — tight reverse
 *   E  uturn           (Curve90 + Straight + Curve90 + Straight) — wide reverse
 */
import {
  buildOrthogonalTrackPath,
  computeFilletCurve,
  computeSegmentBetween,
  computeUTurn180,
  ChainSizing,
} from './trackChain';
import { Vec } from './pointDrawChain';

export type PointDrawRoutePattern =
  | 'straight'
  | 'turn90'
  | 'straight_turn90'
  | 'curve180'
  | 'uturn';

export interface PointDrawRouteInput {
  head: Vec;
  /** Arrival heading into `head` (0=E, 90=S, 180=W, 270=N). Null before the first leg. */
  travelHeading: number | null;
  cursor: Vec;
  radius: number;
  /** Minimum visible straight between two curves (same convention as buildChainPieces). */
  minStraight: number;
  /** Length of the inbound straight ending at `head` (for fillet room). */
  inboundStraightLength: number | null;
}

export interface PointDrawRouteSolution {
  pattern: PointDrawRoutePattern;
  /** Manhattan corner vertices to append after `head` (last = landing point). */
  appendedVertices: Vec[];
  /** Preview strokes as flat [x0,y0,x1,y1,...] polylines. */
  previewPaths: number[][];
  endPoint: Vec;
  exitHeading: number;
  valid: boolean;
}

const EPS = 1e-6;

const normDeg = (deg: number): number => ((deg % 360) + 360) % 360;

const dir = (headingDeg: number): Vec => {
  const r = (headingDeg * Math.PI) / 180;
  return { x: Math.cos(r), y: Math.sin(r) };
};

const add = (a: Vec, b: Vec, s = 1): Vec => ({ x: a.x + b.x * s, y: a.y + b.y * s });

/**
 * Exact unit vector for cardinal headings. `dir` goes through cos/sin, which
 * leaves ~1e-16 dust on the zero component; harmless for previews but it ends
 * up in committed vertex coordinates.
 */
const axisDir = (headingDeg: number): Vec => {
  switch (normDeg(headingDeg)) {
    case 0:
      return { x: 1, y: 0 };
    case 90:
      return { x: 0, y: 1 };
    case 180:
      return { x: -1, y: 0 };
    case 270:
      return { x: 0, y: -1 };
    default:
      return dir(headingDeg);
  }
};

const dist = (a: Vec, b: Vec): number => Math.hypot(b.x - a.x, b.y - a.y);

/** Dominant-axis landing from head toward cursor (existing Point Draw projection). */
export function dominantAxisEnd(head: Vec, cursor: Vec): { end: Vec; heading: number } {
  const dx = cursor.x - head.x;
  const dy = cursor.y - head.y;
  if (Math.abs(dx) < EPS && Math.abs(dy) < EPS) {
    return { end: { ...head }, heading: 0 };
  }
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  const end = horizontal ? { x: cursor.x, y: head.y } : { x: head.x, y: cursor.y };
  const heading = horizontal ? (dx > 0 ? 0 : 180) : dy > 0 ? 90 : 270;
  return { end, heading };
}

/**
 * Cursor is "behind" the current travel direction when its projection onto the
 * forward unit vector is negative — i.e. it lies in the half-plane opposite the
 * inbound heading.
 */
export function isCursorBehind(head: Vec, travelHeading: number, cursor: Vec): boolean {
  const f = dir(travelHeading);
  const to = { x: cursor.x - head.x, y: cursor.y - head.y };
  return to.x * f.x + to.y * f.y < -EPS;
}

/** Relative turn from `fromHeading` to `toHeading` in [0, 360). */
export function turnDeg(fromHeading: number, toHeading: number): number {
  return normDeg(toHeading - fromHeading);
}

/** Pick CW (+1) or CCW (−1) U-turn side toward the cursor; default CW. */
function uTurnSign(head: Vec, travelHeading: number, cursor: Vec): 1 | -1 {
  const f = dir(travelHeading);
  const to = { x: cursor.x - head.x, y: cursor.y - head.y };
  // Screen y-down: cross z = f.x*to.y - f.y*to.x; positive ⇒ cursor to CW side.
  const cross = f.x * to.y - f.y * to.x;
  if (Math.abs(cross) < EPS) return 1;
  return cross > 0 ? 1 : -1;
}

/** Signed lateral offset (positive = CW side of travel axis). */
function signedLateral(head: Vec, travelHeading: number, cursor: Vec): number {
  const f = dir(travelHeading);
  const to = { x: cursor.x - head.x, y: cursor.y - head.y };
  return f.x * to.y - f.y * to.x;
}

/** Absolute lateral distance of `cursor` from the travel axis through `head`. */
function lateralOffset(head: Vec, travelHeading: number, cursor: Vec): number {
  return Math.abs(signedLateral(head, travelHeading, cursor));
}

/** TEMP debug — set false / remove after investigating Curve180 flip. */
const DEBUG_CURVE180_DECISIONS = true;

export type Curve180CandidateDebug = {
  sign: 1 | -1;
  side: 'CW' | 'CCW';
  valid: boolean;
  rejectReason: string | null;
  /** Lower is better when comparing valid candidates (currently unused for selection). */
  score: number | null;
  center: Vec | null;
  rotation: number | null;
  curveSweepSign: 1 | -1 | null;
  exit: Vec | null;
  exitHeading: number | null;
  v1: Vec | null;
  end: Vec | null;
};

function buildCurve180Candidate(
  input: PointDrawRouteInput,
  sizing: ChainSizing,
  sign: 1 | -1
): Curve180CandidateDebug {
  const { head, cursor, travelHeading, radius: R, minStraight, inboundStraightLength } = input;
  const side: 'CW' | 'CCW' = sign === 1 ? 'CW' : 'CCW';
  const base: Curve180CandidateDebug = {
    sign,
    side,
    valid: false,
    rejectReason: null,
    score: null,
    center: null,
    rotation: null,
    curveSweepSign: null,
    exit: null,
    exitHeading: null,
    v1: null,
    end: null,
  };

  if (travelHeading == null) {
    return { ...base, rejectReason: 'no-travel-heading' };
  }
  if (!isCursorBehind(head, travelHeading, cursor)) {
    return { ...base, rejectReason: 'cursor-not-behind' };
  }
  if (inboundStraightLength == null || inboundStraightLength < minStraight - EPS) {
    return { ...base, rejectReason: 'inbound-too-short' };
  }
  const lat = lateralOffset(head, travelHeading, cursor);
  const lateralLimit = 2 * R + minStraight * 0.5;
  if (lat > lateralLimit + EPS) {
    return {
      ...base,
      rejectReason: `lateral-too-wide (${lat.toFixed(3)} > ${lateralLimit.toFixed(3)})`,
    };
  }

  const perpHeading = normDeg(travelHeading + (sign === 1 ? 90 : -90));
  const backHeading = normDeg(travelHeading + 180);
  const v1 = add(head, dir(perpHeading), 2 * R);
  const back = dir(backHeading);
  const toCursor = { x: cursor.x - v1.x, y: cursor.y - v1.y };
  let t = toCursor.x * back.x + toCursor.y * back.y;
  if (t < minStraight) t = minStraight;
  const end = add(v1, back, t);

  const curve = computeUTurn180(head, travelHeading, { ...sizing, radiusUnits: R }, sign);
  if (dist(curve.exit, v1) > 1e-3) {
    return {
      ...base,
      rejectReason: `exit-mismatch (dist=${dist(curve.exit, v1).toFixed(4)})`,
      center: { ...curve.position },
      rotation: curve.rotation,
      curveSweepSign: curve.curveSweepSign ?? null,
      exit: { ...curve.exit },
      v1,
      end,
    };
  }

  // Score: prefer the side the cursor is already on (absolute signed lateral on that side).
  // NOTE: selection does NOT currently use this score — it uses uTurnSign alone.
  const cross = signedLateral(head, travelHeading, cursor);
  const sideAgreement = sign === 1 ? cross : -cross; // >0 when cursor is on this candidate's side
  const score = -sideAgreement; // lower = better agreement with cursor side

  return {
    sign,
    side,
    valid: true,
    rejectReason: null,
    score,
    center: { ...curve.position },
    rotation: curve.rotation,
    curveSweepSign: curve.curveSweepSign ?? sign,
    exit: { ...curve.exit },
    exitHeading: backHeading,
    v1,
    end,
  };
}

function logCurve180Decision(
  input: PointDrawRouteInput,
  candidates: Curve180CandidateDebug[],
  selectedSign: 1 | -1 | null,
  patternSelected: PointDrawRoutePattern | 'invalid' | 'uturn-fallback',
  selectionReason: string
): void {
  if (!DEBUG_CURVE180_DECISIONS) return;
  const { head, cursor, travelHeading, radius: R, minStraight } = input;
  const cross = travelHeading != null ? signedLateral(head, travelHeading, cursor) : null;
  console.log('[PointDraw Curve180 decision]', {
    mouse: { ...cursor },
    head: { ...head },
    travelHeading,
    radius: R,
    minStraight,
    signedLateral: cross,
    absLateral: cross != null ? Math.abs(cross) : null,
    lateralTieEps: EPS,
    onAxisTieBreaksTo: 'CW (+1)',
    uTurnSignWouldPick:
      travelHeading != null ? uTurnSign(head, travelHeading, cursor) : null,
    patternSelected,
    selectionReason,
    selectedSign,
    candidates,
    note:
      'Selection is NOT scored between CW/CCW — only uTurnSign(cross product) picks one side. Both candidates are often valid.',
  });
}

/**
 * Build preview strokes for a Manhattan corner polyline that starts at `head`
 * (already the last committed vertex) with known inbound heading.
 */
function previewFromAppended(
  head: Vec,
  travelHeading: number | null,
  appended: Vec[],
  radius: number,
  minStraight: number,
  sizing: ChainSizing
): number[][] {
  if (appended.length === 0) return [];
  const poly =
    travelHeading != null
      ? [
          add(head, dir(travelHeading), -(radius + minStraight)),
          head,
          ...appended,
        ]
      : [head, ...appended];
  const result = buildOrthogonalTrackPath(
    poly,
    { ...sizing, radiusUnits: radius },
    { minStraightUnits: minStraight }
  );
  return result.ok ? result.pieces.map((piece) => piece.points) : [];
}

/**
 * Pattern C — keep running along the travel axis, then corner toward the cursor.
 *
 * `dominantAxisEnd` collapses the cursor onto whichever axis it has travelled
 * furthest along, so swinging sideways mid-leg either ignored the sideways move
 * or (once it dominated) pivoted the corner all the way back to `head`, throwing
 * away the straight already drawn. Here the corner lands where the operator
 * actually turned — the cursor's projection on the travel axis — and the leg
 * continues perpendicular to reach the cursor, giving a true L.
 *
 * Both legs must clear a fillet; otherwise a plain straight (tiny sideways
 * drift) or a corner at `head` (barely moved forward) is the better read, so
 * this yields to them.
 */
function tryStraightTurn90(
  input: PointDrawRouteInput,
  sizing: ChainSizing
): PointDrawRouteSolution | null {
  const { head, cursor, travelHeading, radius: R, minStraight } = input;
  if (travelHeading == null) return null;

  const f = axisDir(travelHeading);
  const to = { x: cursor.x - head.x, y: cursor.y - head.y };
  const along = to.x * f.x + to.y * f.y;
  const cross = signedLateral(head, travelHeading, cursor);
  const lateral = Math.abs(cross);

  const minLeg = R + minStraight;
  if (along < minLeg - EPS) return null;
  if (lateral < minLeg - EPS) return null;

  const sign: 1 | -1 = cross > 0 ? 1 : -1;
  const perpHeading = normDeg(travelHeading + (sign === 1 ? 90 : -90));
  const corner = add(head, f, along);
  const end = add(corner, axisDir(perpHeading), lateral);

  // Not `previewFromAppended`: that keeps `head` as an interior vertex, and the
  // path builder rejects the resulting zero-degree corner ('invalid-corner')
  // because this leg runs straight through the head rather than bending at it.
  const stub = add(head, f, -(R + minStraight));
  const built = buildOrthogonalTrackPath(
    [stub, corner, end],
    { ...sizing, radiusUnits: R },
    { minStraightUnits: minStraight }
  );
  if (!built.ok) return null;
  const previewPaths = built.pieces.map((piece) => piece.points);
  // Straight → curve → straight. Fewer means the fillet did not fit.
  if (previewPaths.length < 3) return null;

  return {
    pattern: 'straight_turn90',
    appendedVertices: [corner, end],
    previewPaths,
    endPoint: end,
    exitHeading: perpHeading,
    valid: true,
  };
}

function tryStraight(input: PointDrawRouteInput, sizing: ChainSizing): PointDrawRouteSolution | null {
  const { head, cursor, travelHeading } = input;
  const { end, heading } = dominantAxisEnd(head, cursor);
  if (dist(head, end) < EPS) return null;
  if (travelHeading != null && normDeg(heading) !== normDeg(travelHeading)) return null;

  return {
    pattern: 'straight',
    appendedVertices: [end],
    previewPaths: [computeSegmentBetween(head, end, sizing).points],
    endPoint: end,
    exitHeading: heading,
    valid: true,
  };
}

function tryTurn90(input: PointDrawRouteInput, sizing: ChainSizing): PointDrawRouteSolution | null {
  const { head, cursor, travelHeading, radius: R, inboundStraightLength } = input;
  if (travelHeading == null) return null;
  if (
    inboundStraightLength == null ||
    inboundStraightLength < R + input.minStraight - EPS
  ) {
    return null;
  }

  const { end, heading } = dominantAxisEnd(head, cursor);
  const legLen = dist(head, end);
  if (legLen < R + input.minStraight - EPS) return null;

  const t = turnDeg(travelHeading, heading);
  if (t !== 90 && t !== 270) return null;

  const sign: 1 | -1 = t === 90 ? 1 : -1;
  const f = dir(travelHeading);
  const entry = add(head, f, -R);
  const curve = computeFilletCurve(entry, travelHeading, { ...sizing, radiusUnits: R }, sign);
  const straight = computeSegmentBetween(curve.exit, end, sizing);

  return {
    pattern: 'turn90',
    appendedVertices: [end],
    previewPaths: [curve.points, straight.points],
    endPoint: end,
    exitHeading: heading,
    valid: true,
  };
}

/**
 * Pattern D — single Curve180 U-turn when a tight diameter reverse fits.
 *
 * Chosen when the cursor is behind and does not ask for a reverse lane farther
 * out than one Curve180 diameter (2R). Vertices are still Manhattan
 * (`head → exit@2R → end`) so `buildOrthogonalTrackPath` emits the Curve180
 * primitive (not two Curve90s).
 *
 * IMPORTANT: CW and CCW are both often valid. The winner is NOT scored — it is
 * picked solely by `uTurnSign` (cross product of travel × cursor). Near the
 * travel axis (|cross| < 1e-6) this tie-breaks to CW, so a 1-pixel / 1-grid
 * nudge across the axis flips the bulge (the "inverted Curve180" symptom).
 */
function tryCurve180(input: PointDrawRouteInput, sizing: ChainSizing): PointDrawRouteSolution | null {
  const { head, cursor, travelHeading, radius: R, minStraight } = input;

  // Evaluate both sides for diagnostics; selection still uses uTurnSign only.
  const candidates = [
    buildCurve180Candidate(input, sizing, 1),
    buildCurve180Candidate(input, sizing, -1),
  ];
  const preferredSign =
    travelHeading != null ? uTurnSign(head, travelHeading, cursor) : null;
  const selected =
    preferredSign != null
      ? candidates.find((c) => c.sign === preferredSign && c.valid) ?? null
      : null;
  const otherValid = candidates.filter((c) => c.valid && c.sign !== preferredSign);

  if (!selected) {
    const reason =
      candidates
        .map((c) => `${c.side}:${c.rejectReason ?? 'ok-but-not-preferred'}`)
        .join('; ') || 'unknown';
    logCurve180Decision(input, candidates, preferredSign, 'uturn-fallback', reason);
    return null;
  }

  logCurve180Decision(
    input,
    candidates,
    selected.sign,
    'curve180',
    otherValid.length > 0
      ? `uTurnSign=${selected.sign} (${selected.side}); ${otherValid.length} other valid candidate(s) ignored (no score compare)`
      : `uTurnSign=${selected.sign} (${selected.side}); only valid side`
  );

  const sign = selected.sign;
  const v1 = selected.v1!;
  const end = selected.end!;
  const backHeading = selected.exitHeading!;
  const curve = computeUTurn180(head, travelHeading!, { ...sizing, radiusUnits: R }, sign);
  const exitStraight = computeSegmentBetween(curve.exit, end, sizing);
  const appended = [v1, end];
  const previewPaths = previewFromAppended(head, travelHeading, appended, R, minStraight, sizing);
  if (previewPaths.length < 2) {
    return {
      pattern: 'curve180',
      appendedVertices: appended,
      previewPaths: [curve.points, exitStraight.points],
      endPoint: end,
      exitHeading: backHeading,
      valid: true,
    };
  }

  return {
    pattern: 'curve180',
    appendedVertices: appended,
    previewPaths,
    endPoint: end,
    exitHeading: backHeading,
    valid: true,
  };
}

/**
 * Pattern E — U-turn composed of Curve90 → Straight (≥ minStraight) → Curve90 → Straight.
 * Expressed as Manhattan corners so the shared fillet generator emits legal primitives.
 * Used when a single Curve180 cannot span the requested turnaround width.
 */
function tryUTurn(input: PointDrawRouteInput, sizing: ChainSizing): PointDrawRouteSolution | null {
  const { head, cursor, travelHeading, radius: R, minStraight, inboundStraightLength } = input;
  if (travelHeading == null) return null;
  if (!isCursorBehind(head, travelHeading, cursor)) return null;
  if (inboundStraightLength == null || inboundStraightLength < R + minStraight - EPS) return null;

  const sign = uTurnSign(head, travelHeading, cursor);
  // Successive filleted corners need 2R + minStraight between them, but that is
  // only a floor. The reverse lane otherwise follows the cursor, so a U-turn
  // aimed well off the axis is drawn at that depth instead of always hugging
  // the outbound rail at the tightest legal spacing.
  const cornerLeg = Math.max(
    2 * R + minStraight,
    lateralOffset(head, travelHeading, cursor)
  );
  const exitAfter = R + minStraight; // open end after last fillet

  const perpHeading = normDeg(travelHeading + (sign === 1 ? 90 : -90));
  const backHeading = normDeg(travelHeading + 180);
  const perp = axisDir(perpHeading);
  const back = axisDir(backHeading);

  const v1 = add(head, perp, cornerLeg);

  // Project cursor onto the reverse-direction line through v1; require enough
  // length past the second fillet.
  const toCursor = { x: cursor.x - v1.x, y: cursor.y - v1.y };
  let t = toCursor.x * back.x + toCursor.y * back.y;
  if (t < exitAfter) t = exitAfter;
  const end = add(v1, back, t);

  // Optional intermediate corner not needed: v1→end is already reverse heading.
  const appended = [v1, end];
  const previewPaths = previewFromAppended(head, travelHeading, appended, R, minStraight, sizing);

  // Sanity: two curves + at least one reverse straight.
  if (previewPaths.length < 2) return null;

  return {
    pattern: 'uturn',
    appendedVertices: appended,
    previewPaths,
    endPoint: end,
    exitHeading: backHeading,
    valid: true,
  };
}

/**
 * Evaluate routing patterns and return the simplest legal solution.
 */
export function solvePointDrawRoute(
  input: PointDrawRouteInput,
  sizing: ChainSizing
): PointDrawRouteSolution {
  const invalid = (): PointDrawRouteSolution => ({
    pattern: 'straight',
    appendedVertices: [],
    previewPaths: [],
    endPoint: input.head,
    exitHeading: input.travelHeading ?? 0,
    valid: false,
  });

  const dx = input.cursor.x - input.head.x;
  const dy = input.cursor.y - input.head.y;
  if (Math.abs(dx) < EPS && Math.abs(dy) < EPS) return invalid();

  // Prefer reverse patterns when the cursor is behind — a single Curve90 cannot reverse.
  if (input.travelHeading != null && isCursorBehind(input.head, input.travelHeading, input.cursor)) {
    const curve180 = tryCurve180(input, sizing);
    if (curve180?.valid) return curve180;
    const uturn = tryUTurn(input, sizing);
    if (uturn?.valid) return uturn;
    return invalid();
  }

  // Ahead-and-to-the-side is an L, so this must beat `tryStraight` — which would
  // otherwise swallow the sideways move whenever the forward run is longer.
  const straightTurn90 = tryStraightTurn90(input, sizing);
  if (straightTurn90?.valid) return straightTurn90;

  const straight = tryStraight(input, sizing);
  if (straight?.valid) return straight;

  const turn90 = tryTurn90(input, sizing);
  if (turn90?.valid) return turn90;

  // A direction change that cannot fit the configured Curve90 is invalid.
  // Never fall back to a perpendicular straight, which would commit a hard L.
  return invalid();
}
